// Webclient → Contacts. Three sections:
//   • Directory     — the company directory (extensions in the user's domain).
//   • My Contacts   — personal contacts CRUD, plus per-contact department sharing.
//   • Shared with me — contacts shared into my departments (owner-curates model:
//     rows I own are editable/unshareable, everyone else's are read-only).
// All personal/shared data is served by /api/me/contacts (scope from the JWT).

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Pencil, Trash2, Share2 } from 'lucide-react';
import { api, MyContact, SharedContact, ContactInput } from '../lib/api';
import { groupSharedByDepartment } from '../lib/contactsGroup';
import { useActiveTenant } from '../lib/tenantScope';
import { Dialog } from '../components/Dialog';

type Tab = 'directory' | 'mine' | 'shared';
type Entry = { extension: string; name: string };

function initials(s: string) {
  return (s || '').trim().slice(0, 2).toUpperCase() || '?';
}

export default function Contacts() {
  const [tab, setTab] = useState<Tab>('directory');

  return (
    <div className="h-full flex flex-col min-h-0">
      <div className="h-12 shrink-0 flex items-center gap-1 px-4 border-b border-border">
        {([['directory', 'Directory'], ['mine', 'My Contacts'], ['shared', 'Shared with me']] as [Tab, string][]).map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === id ? 'bg-elevated text-primary' : 'text-textSecondary hover:bg-elevated'}`}>
            {label}
          </button>
        ))}
      </div>
      {tab === 'directory' && <DirectorySection />}
      {tab === 'mine' && <MyContactsSection />}
      {tab === 'shared' && <SharedSection />}
    </div>
  );
}

// ── Directory ───────────────────────────────────────────────────────────────
function DirectorySection() {
  const [term, setTerm] = useState('');
  const tenant = useActiveTenant();
  const dir = useQuery({ queryKey: ['portal-directory', tenant], queryFn: () => api.portal.directory() });

  const filtered = useMemo(() => {
    const t = term.trim().toLowerCase();
    const list = dir.data || [];
    if (!t) return list;
    return list.filter(e => e.extension.includes(t) || (e.name || '').toLowerCase().includes(t));
  }, [dir.data, term]);

  return (
    <>
      <div className="shrink-0 flex items-center gap-3 px-4 py-2 border-b border-border">
        <input value={term} onChange={e => setTerm(e.target.value)} placeholder="Search name or extension…"
          className="input py-1 text-sm flex-1 max-w-xs" />
      </div>
      <div className="flex-1 overflow-y-auto">
        {dir.isLoading && <div className="p-4 text-sm text-textSecondary">Loading…</div>}
        {dir.data && filtered.length === 0 && <div className="p-4 text-sm text-textSecondary">No matches.</div>}
        {filtered.map((e: Entry) => (
          <div key={e.extension} className="flex items-center gap-3 px-4 py-2.5 border-b border-border/50 hover:bg-elevated">
            <div className="shrink-0 w-9 h-9 rounded-full bg-elevated flex items-center justify-center text-sm font-semibold text-textSecondary">
              {initials(e.name || e.extension)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{e.name || `Extension ${e.extension}`}</div>
              <div className="text-xs text-textSecondary">Ext {e.extension}</div>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

// ── My Contacts ─────────────────────────────────────────────────────────────
function MyContactsSection() {
  const qc = useQueryClient();
  const tenant = useActiveTenant();
  const [term, setTerm] = useState('');
  const [editing, setEditing] = useState<MyContact | null>(null); // row being edited
  const [adding, setAdding] = useState(false);
  const [sharing, setSharing] = useState<MyContact | null>(null);

  const list = useQuery({ queryKey: ['my-contacts', tenant], queryFn: () => api.contacts.list() });

  const filtered = useMemo(() => {
    const t = term.trim().toLowerCase();
    const rows = list.data || [];
    if (!t) return rows;
    return rows.filter(c =>
      (c.name || '').toLowerCase().includes(t) ||
      (c.number || '').includes(t) ||
      (c.company || '').toLowerCase().includes(t));
  }, [list.data, term]);

  const del = useMutation({
    mutationFn: (id: string) => api.contacts.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['my-contacts'] }),
  });

  return (
    <>
      <div className="shrink-0 flex items-center gap-3 px-4 py-2 border-b border-border">
        <input value={term} onChange={e => setTerm(e.target.value)} placeholder="Search contacts…"
          className="input py-1 text-sm flex-1 max-w-xs" />
        <button onClick={() => setAdding(true)} className="btn-primary inline-flex items-center gap-1 shrink-0">
          <Plus size={14} /> Add
        </button>
      </div>
      <div className="flex-1 overflow-y-auto">
        {list.isLoading && <div className="p-4 text-sm text-textSecondary">Loading…</div>}
        {list.data && filtered.length === 0 && <div className="p-4 text-sm text-textSecondary">No contacts yet.</div>}
        {filtered.map(c => (
          <div key={c.contact_uuid} className="flex items-center gap-3 px-4 py-2.5 border-b border-border/50 hover:bg-elevated group">
            <div className="shrink-0 w-9 h-9 rounded-full bg-elevated flex items-center justify-center text-sm font-semibold text-textSecondary">
              {initials(c.name)}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">{c.name}</div>
              <div className="text-xs text-textSecondary truncate">
                {c.number}{c.company ? ` · ${c.company}` : ''}{c.title ? ` · ${c.title}` : ''}
              </div>
            </div>
            <div className="shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button onClick={() => setSharing(c)} title="Share to department"
                className="p-2 rounded-lg text-textSecondary hover:text-primary hover:bg-bg"><Share2 size={16} /></button>
              <button onClick={() => setEditing(c)} title="Edit"
                className="p-2 rounded-lg text-textSecondary hover:text-primary hover:bg-bg"><Pencil size={16} /></button>
              <button onClick={() => { if (confirm(`Delete ${c.name}?`)) del.mutate(c.contact_uuid); }} title="Delete"
                className="p-2 rounded-lg text-textSecondary hover:text-danger hover:bg-bg"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>
      {(adding || editing) && (
        <ContactForm contact={editing} onClose={() => { setAdding(false); setEditing(null); }} />
      )}
      {sharing && <ShareDialog contact={sharing} onClose={() => setSharing(null)} />}
    </>
  );
}

// ── Add / Edit form ─────────────────────────────────────────────────────────
function ContactForm({ contact, onClose }: { contact: MyContact | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState<ContactInput>({
    name: contact?.name || '',
    number: contact?.number || '',
    email: contact?.email || '',
    company: contact?.company || '',
    title: contact?.title || '',
  });
  const set = (patch: Partial<ContactInput>) => setForm(f => ({ ...f, ...patch }));
  const valid = form.name.trim() !== '' && form.number.trim() !== '';

  const save = useMutation({
    mutationFn: async () => {
      const body: ContactInput = {
        name: form.name.trim(),
        number: form.number.trim(),
        email: form.email?.trim() || undefined,
        company: form.company?.trim() || undefined,
        title: form.title?.trim() || undefined,
      };
      if (contact) await api.contacts.update(contact.contact_uuid, body);
      else await api.contacts.create(body);
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['my-contacts'] }); qc.invalidateQueries({ queryKey: ['shared-contacts'] }); onClose(); },
  });

  return (
    <Dialog open onClose={onClose} title={contact ? 'Edit contact' : 'Add contact'} size="sm"
      footer={<>
        <button onClick={onClose} className="btn-ghost">Cancel</button>
        <button onClick={() => save.mutate()} disabled={!valid || save.isPending} className="btn-primary disabled:opacity-50">
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </>}>
      <div className="space-y-3">
        <label className="block">
          <span className="text-xs text-textSecondary">Name</span>
          <input value={form.name} onChange={e => set({ name: e.target.value })} className="input mt-1" placeholder="Full name" />
        </label>
        <label className="block">
          <span className="text-xs text-textSecondary">Number</span>
          <input value={form.number} onChange={e => set({ number: e.target.value })} className="input mt-1" placeholder="Phone number" />
        </label>
        <label className="block">
          <span className="text-xs text-textSecondary">Email <span className="opacity-60">(optional)</span></span>
          <input value={form.email} onChange={e => set({ email: e.target.value })} className="input mt-1" placeholder="name@example.com" />
        </label>
        <label className="block">
          <span className="text-xs text-textSecondary">Company <span className="opacity-60">(optional)</span></span>
          <input value={form.company} onChange={e => set({ company: e.target.value })} className="input mt-1" />
        </label>
        <label className="block">
          <span className="text-xs text-textSecondary">Title <span className="opacity-60">(optional)</span></span>
          <input value={form.title} onChange={e => set({ title: e.target.value })} className="input mt-1" />
        </label>
        {save.isError && <div className="text-xs text-danger">Could not save. Check the fields and try again.</div>}
      </div>
    </Dialog>
  );
}

// ── Share to department ─────────────────────────────────────────────────────
function ShareDialog({ contact, onClose }: { contact: MyContact; onClose: () => void }) {
  const qc = useQueryClient();
  const tenant = useActiveTenant();
  const groups = useQuery({ queryKey: ['my-contact-groups', tenant], queryFn: () => api.contacts.groups() });
  const shares = useQuery({ queryKey: ['my-contact-shares', tenant, contact.contact_uuid], queryFn: () => api.contacts.shares(contact.contact_uuid) });
  const sharedInto = new Set((shares.data || []).map(s => s.group_uuid));

  const toggle = useMutation({
    mutationFn: ({ group_uuid, on }: { group_uuid: string; on: boolean }) =>
      on ? api.contacts.share(contact.contact_uuid, group_uuid) : api.contacts.unshare(contact.contact_uuid, group_uuid),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['my-contact-shares'] });
      qc.invalidateQueries({ queryKey: ['shared-contacts'] });
    },
  });

  const loading = groups.isLoading || shares.isLoading;

  return (
    <Dialog open onClose={onClose} title={`Share “${contact.name}”`} size="sm"
      footer={<button onClick={onClose} className="btn-primary">Done</button>}>
      <p className="text-sm text-textSecondary mb-3">Choose which of your departments can see this contact.</p>
      {loading && <div className="text-sm text-textSecondary">Loading…</div>}
      {groups.data && groups.data.length === 0 && (
        <div className="text-sm text-textSecondary">You are not a member of any department.</div>
      )}
      <div className="space-y-1">
        {(groups.data || []).map(g => {
          const on = sharedInto.has(g.group_uuid);
          return (
            <label key={g.group_uuid} className="flex items-center gap-3 px-2 py-2 rounded-md hover:bg-elevated cursor-pointer">
              <input type="checkbox" checked={on} disabled={toggle.isPending}
                onChange={e => toggle.mutate({ group_uuid: g.group_uuid, on: e.target.checked })} />
              <span className="text-sm flex-1 truncate">{g.name}</span>
              <span className="text-xs text-textSecondary">{g.role}</span>
            </label>
          );
        })}
      </div>
      {toggle.isError && <div className="text-xs text-danger mt-2">Could not update sharing. Try again.</div>}
    </Dialog>
  );
}

// ── Shared with me ──────────────────────────────────────────────────────────
function SharedSection() {
  const qc = useQueryClient();
  const tenant = useActiveTenant();
  const shared = useQuery({ queryKey: ['shared-contacts', tenant], queryFn: () => api.contacts.shared() });

  const grouped = useMemo(() => groupSharedByDepartment(shared.data), [shared.data]);

  const unshare = useMutation({
    mutationFn: (row: SharedContact) => api.contacts.unshare(row.contact_uuid, row.group_uuid),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['shared-contacts'] }),
  });

  return (
    <div className="flex-1 overflow-y-auto">
      {shared.isLoading && <div className="p-4 text-sm text-textSecondary">Loading…</div>}
      {shared.data && grouped.length === 0 && <div className="p-4 text-sm text-textSecondary">Nothing shared with you yet.</div>}
      {grouped.map(([groupName, rows]) => (
        <div key={groupName}>
          <div className="px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-textSecondary bg-elevated/50 border-b border-border/50 sticky top-0">
            {groupName}
          </div>
          {rows.map(row => (
            <div key={row.share_uuid} className="flex items-center gap-3 px-4 py-2.5 border-b border-border/50 hover:bg-elevated group">
              <div className="shrink-0 w-9 h-9 rounded-full bg-elevated flex items-center justify-center text-sm font-semibold text-textSecondary">
                {initials(row.name)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{row.name}</div>
                <div className="text-xs text-textSecondary truncate">
                  {row.number}{row.company ? ` · ${row.company}` : ''}{row.title ? ` · ${row.title}` : ''}
                </div>
              </div>
              {row.is_owner ? (
                <button onClick={() => { if (confirm(`Stop sharing ${row.name} with ${groupName}?`)) unshare.mutate(row); }}
                  title="Unshare" className="shrink-0 p-2 rounded-lg text-textSecondary hover:text-danger hover:bg-bg opacity-0 group-hover:opacity-100 transition-opacity">
                  <Trash2 size={16} />
                </button>
              ) : (
                <span className="shrink-0 text-xs text-textSecondary">shared by {row.shared_by}</span>
              )}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
